import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type {
  SisBundleSummary,
  UploadStatus,
  UploadSummary,
} from "@canvas-sync/shared";
import { api } from "../api/client";
import { UploadsTimeline } from "../components/UploadsTimeline";
import { submissionDate, type SubmissionItem } from "../lib/submissions";
import { useRole } from "../lib/roleContext";
import { POLL, usePoll } from "../lib/usePoll";
import { FilterBar, type FilterOption } from "../components/FilterBar";
import {
  CANVAS_ENV_OPTIONS,
  EMPTY_FILTERS,
  applyFilters,
  type Accessors,
  type FilterState,
} from "../lib/filters";

const NON_TERMINAL_UPLOAD = new Set<UploadStatus>([
  "SUBMITTING",
  "SUBMITTED",
  "IMPORTING",
]);

// Upload statuses plus the bundle statuses that can appear in the unified
// feed. Bundle values are prefixed so they can't collide with the upload
// enum (both worlds have a "SUBMITTED" that means different things).
const STATUS_OPTIONS: FilterOption[] = [
  { value: "SUBMITTING", label: "Sending" },
  { value: "SUBMITTED", label: "Waiting for Canvas" },
  { value: "IMPORTING", label: "Processing" },
  { value: "IMPORTED", label: "Imported" },
  { value: "IMPORTED_WITH_MESSAGES", label: "Imported with warnings" },
  { value: "FAILED", label: "Failed" },
  { value: "FAILED_WITH_MESSAGES", label: "Partially failed" },
  { value: "ABORTED", label: "Canceled in Canvas" },
  { value: "ERROR", label: "Could not reach Canvas" },
  { value: "BUNDLE_SUBMITTED", label: "Bundle: full SIS drop" },
  { value: "BUNDLE_SUBMITTED_FIX_ONLY", label: "Bundle: ghost fix only" },
  { value: "BUNDLE_SUBMITTED_SIS_ONLY", label: "Bundle: SIS only (no fix)" },
  { value: "BUNDLE_FAILED", label: "Bundle: failed after submit" },
];

const TRIGGER_OPTIONS: FilterOption[] = [
  { value: "manual", label: "Manual" },
  { value: "auto", label: "Automatic" },
];

const ITEM_ACCESSORS: Accessors<SubmissionItem> = {
  status: (it) =>
    it.kind === "upload" ? it.upload.status : `BUNDLE_${it.bundle.status}`,
  // Bundles are the automated pipeline; a manual re-trigger is the
  // bundle-world equivalent of a manual upload.
  trigger: (it) =>
    it.kind === "upload"
      ? it.upload.trigger
      : it.bundle.triggeredBy === "manual-retrigger"
        ? "manual"
        : "auto",
  env: (it) =>
    it.kind === "upload" ? (it.upload.canvasEnv ?? "test") : it.bundle.env,
  date: submissionDate,
  duration: (it) => (it.kind === "upload" ? it.upload.durationMs : undefined),
  searchFields: (it) =>
    it.kind === "upload"
      ? [
          it.upload.uploadId,
          it.upload.sisImportId,
          it.upload.relatedRunId,
          it.upload.trigger,
          it.upload.status,
          it.upload.canvasEnv,
        ]
      : [
          it.bundle.bundleId,
          it.bundle.dropId,
          it.bundle.sisImportId,
          it.bundle.ghostDetectionRunId,
          it.bundle.status,
          it.bundle.env,
          "bundle",
        ],
};

export function UploadsPage() {
  const { isAdmin } = useRole();
  const [uploads, setUploads] = useState<UploadSummary[] | null>(null);
  const [bundles, setBundles] = useState<SisBundleSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);

  usePoll(
    async () => {
      // allSettled: the unified feed should still show uploads when the
      // bundles fetch hiccups (and vice versa).
      const [u, b] = await Promise.allSettled([
        api.listUploads(200),
        api.listSisBundles({ limit: 200 }),
      ]);
      if (u.status === "fulfilled") setUploads(u.value.uploads);
      if (b.status === "fulfilled") setBundles(b.value.bundles);
      if (u.status === "rejected") {
        setError((u.reason as Error)?.message ?? String(u.reason));
      } else {
        setError(null);
      }
    },
    () =>
      uploads && uploads.some((u) => NON_TERMINAL_UPLOAD.has(u.status))
        ? POLL.LIST_ACTIVE_MS
        : POLL.IDLE_MS,
  );

  // The unified feed: standalone uploads + every SIS bundle that actually
  // POSTed to Canvas (sisImportId set — excludes dry-runs and pre-POST
  // lifecycle states). Newest first. This is the "what did we send to
  // Canvas?" page, so BOTH posting paths must appear — a bundle submission
  // that's invisible here is how operators end up re-sending fixes that
  // already shipped.
  const items = useMemo<SubmissionItem[]>(() => {
    const fromUploads: SubmissionItem[] = (uploads ?? []).map((upload) => ({
      kind: "upload",
      upload,
    }));
    const fromBundles: SubmissionItem[] = (bundles ?? [])
      .filter((b) => b.sisImportId !== undefined)
      .map((bundle) => ({ kind: "bundle", bundle }));
    return [...fromUploads, ...fromBundles].sort((a, z) =>
      submissionDate(z).localeCompare(submissionDate(a)),
    );
  }, [uploads, bundles]);

  const visible = useMemo(
    () => applyFilters(items, filters, ITEM_ACCESSORS),
    [items, filters],
  );

  const loaded = uploads !== null;

  return (
    <section>
      <div className="page-head">
        <h1>Canvas submissions</h1>
        <div className="page-head-actions">
          <div className="muted">
            {loaded ? `${items.length} loaded` : "Loading…"}
          </div>
          {isAdmin && (
            <Link to="/test-upload" className="btn">
              Test submission
            </Link>
          )}
        </div>
      </div>

      {loaded && (
        <FilterBar
          value={filters}
          onChange={setFilters}
          statuses={STATUS_OPTIONS}
          triggers={TRIGGER_OPTIONS}
          envs={CANVAS_ENV_OPTIONS}
          searchPlaceholder="Search by upload ID, bundle ID, Canvas import ID, or sync ID…"
          totalCount={items.length}
          filteredCount={visible.length}
        />
      )}

      <div className="card">
        {error && <div className="error">{error}</div>}
        {!loaded && !error && <div className="muted">Loading…</div>}
        {loaded && items.length === 0 && (
          <div className="muted">
            Nothing has been sent to Canvas yet. Standalone uploads and SIS
            bundle submissions both appear here once they do.
          </div>
        )}
        {items.length > 0 && visible.length === 0 && (
          <div className="muted">No submissions match these filters.</div>
        )}
        {visible.length > 0 && <UploadsTimeline items={visible} />}
      </div>
    </section>
  );
}
